/**
 * ChargesPage — the ReactifCharge loader: designate the ACTIVE lot of a reagent on an analyzer. Loading
 * a new lot decharges the prior active charge for the same (automate, reagent) server-side in one tx
 * (the one-active-charge invariant). The lot picker is sourced from the reagent's FEFO risk list (actif
 * lots only); when none exist we surface a clear empty hint instead of an unusable picker.
 *
 * Full 5-state DoD off the reagent catalog; the lot list is a nested loading region.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  Button,
  Card,
  ErrorState,
  ForbiddenState,
  FormField,
  Input,
  LoadingState,
  OfflineState,
  Select,
  Spinner,
  useToast,
} from "@/ui";
import { useCan } from "@/auth";
import { useT, useUiStore } from "@/app";
import { ApiError } from "@/api";
import { INV_CAPS } from "../caps";
import { useLabId, useReactifs, useRisk, useSetCharge } from "../api";
import { reactifOptions, findReactif } from "../lib";
import { idStr } from "../types";
import { PageHeader } from "../components/PageHeader";

export function ChargesPage() {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const online = useUiStore((s) => s.online);
  const canRead = useCan(INV_CAPS.reactifRead);
  const canSet = useCan(INV_CAPS.chargeSet);

  const catalogQuery = useReactifs(labId, canRead && online);
  const reactifs = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const options = useMemo(() => reactifOptions(reactifs), [reactifs]);
  const charge = useSetCharge();

  const [reactifId, setReactifId] = useState("");
  const [lotId, setLotId] = useState("");
  const [automate, setAutomate] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [touched, setTouched] = useState(false);

  // Lots for the chosen reagent (FEFO, actif) — the active-lot picker source.
  const lotsQuery = useRisk(labId, reactifId || null, canRead && online && reactifId !== "");
  const lots = useMemo(() => (lotsQuery.data ?? []).filter((l) => l.days_remaining >= 0), [lotsQuery.data]);

  const selected = findReactif(reactifs, reactifId);

  // Default the discipline from the chosen reagent; reset the lot when the reagent changes.
  useEffect(() => {
    setLotId("");
    if (selected?.discipline_code) setDiscipline(selected.discipline_code);
  }, [reactifId, selected?.discipline_code]);

  const reactifError = touched && reactifId === "" ? t("inventory.field.required") : null;
  const automateError = touched && automate.trim() === "" ? t("inventory.field.required") : null;
  const lotError = touched && lotId === "" ? t("inventory.field.required") : null;

  const valid = reactifId !== "" && automate.trim() !== "" && lotId !== "";

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canSet) return;
    charge.mutate(
      {
        automate_id: automate.trim(),
        reactif_id: reactifId,
        lot_reactif_id: lotId,
        ...(discipline.trim() !== "" ? { discipline_code: discipline.trim() } : {}),
      },
      {
        onSuccess: (res) => {
          toast.success(t("inventory.charges.success"));
          if (res.decharged_prior) toast.info(t("inventory.charges.dechargedPrior"));
          setLotId("");
          setTouched(false);
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("inventory.field.required")),
      },
    );
  };

  const header = <PageHeader title={t("inventory.charges.title")} subtitle={t("inventory.charges.subtitle")} />;

  if (!canRead) return <ForbiddenState requiredCapabilities={[INV_CAPS.reactifRead]} />;
  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void catalogQuery.refetch()} cached={reactifs.length > 0} />
      </Screen>
    );
  }
  if (catalogQuery.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={4} />
      </Screen>
    );
  }
  if (catalogQuery.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={catalogQuery.error.message}
          code={catalogQuery.error.code}
          onRetry={() => void catalogQuery.refetch()}
        />
      </Screen>
    );
  }

  const lotOptions = lots.map((l) => ({
    value: idStr(l.lot_id),
    label: `${l.numero_lot} · J-${l.days_remaining}`,
  }));
  const showLotEmpty = reactifId !== "" && !lotsQuery.isLoading && lots.length === 0;

  return (
    <Screen>
      {header}

      <Card title={t("inventory.charges.title")} style={{ maxWidth: 560 }}>
        <form onSubmit={submit} noValidate>
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
            <FormField label={t("inventory.reactif.pick")} required {...(reactifError ? { error: reactifError } : {})}>
              {(p) => (
                <Select
                  {...p}
                  autoFocus
                  value={reactifId}
                  invalid={!!reactifError}
                  placeholder={t("inventory.reactif.pick.placeholder")}
                  options={options}
                  onChange={(e) => setReactifId(e.target.value)}
                />
              )}
            </FormField>

            <FormField
              label={t("inventory.charges.lot")}
              hint={t("inventory.charges.lot.hint")}
              required
              {...(lotError ? { error: lotError } : showLotEmpty ? { error: t("inventory.charges.lot.none") } : {})}
            >
              {(p) =>
                lotsQuery.isLoading ? (
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)", minHeight: "var(--nv-min-touch)" }}>
                    <Spinner /> <span style={{ color: "var(--nv-slate)" }}>{t("inventory.reorder.loading")}</span>
                  </div>
                ) : (
                  <Select
                    {...p}
                    value={lotId}
                    invalid={!!lotError || showLotEmpty}
                    disabled={reactifId === "" || lots.length === 0}
                    placeholder={t("inventory.charges.lot.placeholder")}
                    options={lotOptions}
                    onChange={(e) => setLotId(e.target.value)}
                  />
                )
              }
            </FormField>

            <FormField
              label={t("inventory.charges.automate")}
              hint={t("inventory.charges.automate.hint")}
              required
              {...(automateError ? { error: automateError } : {})}
            >
              {(p) => (
                <Input
                  {...p}
                  mono
                  autoComplete="off"
                  value={automate}
                  invalid={!!automateError}
                  onChange={(e) => setAutomate(e.target.value)}
                />
              )}
            </FormField>

            <FormField label={t("inventory.charges.discipline")} hint={t("inventory.charges.discipline.hint")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  autoComplete="off"
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value.toUpperCase())}
                />
              )}
            </FormField>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button type="submit" loading={charge.isPending} disabled={!canSet || !valid}>
                {t("inventory.charges.submit")}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </Screen>
  );
}

function Screen({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>{children}</div>;
}
